<template>
    <div class="payment-received-popover-content">
        <PaymentReceivedHeader :animateIcon="animateIcon" />
        <PaymentSummary :confirmation="confirmation" :displayZeroAmount="true" />

        <div v-if="currentBillState" class="inner">{{ currentBillState }}</div>

        <template v-if="isWaiterOrAdmin">
            <template v-if="showReceiptActionsForOperator">
                <!-- For operator with receipt actions show "more" and "continue" -->
                <button class="btn" type="button" @click="onMoreClick">
                    {{ trans.paymentReceivedPopover.more }}
                </button>

                <button class="btn transparent" type="button" @click="onContinueClick">
                    {{ trans.general.continue }}
                </button>
            </template>

            <template v-if="!showReceiptActionsForOperator">
                <!-- For operator with no receipt actions - show only "continue" -->
                <button class="btn" type="button" @click="onContinueClick">
                    {{ trans.general.continue }}
                </button>
            </template>
        </template>

        <template v-if="isEndUser">
            <button class="btn" type="button" @click="onMoreClick">
                {{ trans.paymentReceivedPopover.more }}
            </button>
        </template>
    </div>
</template>

<script setup lang="ts">
    import { trans } from "@/app/localization/Trans";
    import { Factory } from "@/data/dao/Factory";
    import type { DaoError } from "@/data/dao/service/BaseServiceDao";
    import { CustomerDao } from "@/data/dao/service/CustomerDao";
    import type { IOrderPaymentConfirmation } from "@/data/model/Customer";
    import { exceptionTypes } from "@/data/util/Config";
    import { logError } from "@/shared/logging/errors";
    import { computed, nextTick, onMounted, ref } from "vue";

    import PaymentSummary from "@/app/components/payment/orderReceived/PaymentSummary.vue";
    import PaymentReceivedHeader from "@/app/components/payment/paymentReceived/PaymentReceivedHeader.vue";

    const props = defineProps<{
        confirmation: IOrderPaymentConfirmation;
        isWaiterOrAdmin: boolean;
        showReceiptActionsForOperator: boolean;

        onStartOver?: () => void;
        onReceiptAndMore?: () => void;
        hide?: () => void;
        updateBodyHeight?: () => void;
    }>();

    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

    // State
    const currentBillState = ref(trans.paymentReceivedPopover.unpaid);
    const isEndUser = computed(() => !props.isWaiterOrAdmin);
    const animateIcon = ref(false);

    async function updateCurrentBillState() {
        try {
            const bill = await customerDao.getCurrentBill({
                billId: props.confirmation.billId,
            });
            if (bill == null || bill.unpaid === 0) {
                currentBillState.value = trans.paymentReceivedPopover.fullyPaid;
            } else {
                currentBillState.value = `${trans.paymentReceivedPopover.unpaid} CHF ${bill.unpaid.toFixed(2)}`;
            }
        } catch (ex) {
            logError(ex);
            const e = ex as DaoError;

            //All orders have been fully paid, so the bill has been closed
            if (e.type === exceptionTypes.billDoesNotExistException) {
                currentBillState.value = trans.paymentReceivedPopover.fullyPaid;
            } else {
                currentBillState.value = "";
            }
        } finally {
            await refreshHeight();
        }
    }

    function onMoreClick() {
        if (props.hide) {
            props.hide();
        }
        if (props.onReceiptAndMore) {
            props.onReceiptAndMore();
        }
    }

    function onContinueClick() {
        if (props.onStartOver) {
            props.onStartOver();
        }
        if (props.hide) {
            props.hide();
        }
    }

    // todo: obsolete, remove after confirm flex-height is working
    async function refreshHeight() {
        await nextTick();
        if (props.updateBodyHeight) {
            props.updateBodyHeight();
        }
    }

    // lifecycle
    onMounted(async () => {
        updateCurrentBillState();
        await refreshHeight();

        window.setTimeout(async () => {
            animateIcon.value = true;
            await refreshHeight();
        }, 400);
    });
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../../assets/styles/scss/shared/_variables.scss";

    // todo: obsolete, remove after confirm flex-height is working
    .popover.payment-received-popover {
        > .head {
            height: 46px;
        }

        > .body {
            top: 46px;
        }
    }

    .payment-received-popover-content {
        padding-bottom: 1px; // todo: obsolete, remove after confirm flex-height is working

        > .inner {
            text-align: center;
            margin-top: 14px;
            font-size: 14px;

            > span {
                font-weight: 600;
            }
        }

        > .btn {
            display: block;
            height: 46px;
            margin: 7px 0 18px 0;
            background-color: var(--colorPrimary);
            color: #fff;
            border-radius: 23px;
            width: 100%;
            font-size: 17px;

            &.transparent {
                background-color: transparent;
                color: black;

                @include mixins.hover-and-active() {
                    background-color: transparent;
                }
            }

            @include mixins.hover-and-active() {
                background-color: var(--colorPrimaryHover);
            }
        }
    }
</style>
